import React, { useState, useCallback, useEffect } from "react";
import { Modal, Select, Segmented, ConfigProvider, message } from "antd";
import TextareaAutosize from "react-textarea-autosize";
import { css, Global } from "@emotion/react";

// AI Icon - two sparkles
export const ReloadIcon: React.FC = () => (
  <svg
    viewBox="0 -8 36 36"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      width: "1.6rem",
      height: "1.6rem",
      display: "inline-block",
      verticalAlign: "middle",
    }}
  >
    {/* Large sparkle - vertically centered, perfectly symmetrical */}
    <path d="M12 4 C11.5 10, 7.5 13.5, 2 13.5 C7.5 13.5, 11.5 17, 12 23 C12.5 17, 16.5 13.5, 22 13.5 C16.5 13.5, 12.5 10, 12 4 Z" />

    {/* Small sparkle - upper right, perfectly symmetrical */}
    <path d="M29 -6 C28.6 -3.2, 26.3 -1, 24 -1 C26.3 -1, 28.6 0.8, 29 3.6 C29.4 0.8, 31.7 -1, 34 -1 C31.7 -1, 29.4 -3.2, 29 -6 Z" />
  </svg>
);

interface RefreshSummaryModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit?: (params: {
    customPrompt: string;
    selectedModel: string;
    detailLevel: string;
    familiarityLevel: string;
  }) => void;
}

export const RefreshSummaryModal: React.FC<RefreshSummaryModalProps> = ({
  visible,
  onClose,
  onSubmit,
}) => {
  const [customPrompt, setCustomPrompt] = useState("");
  const [selectedModel, setSelectedModel] = useState("gpt-4.1");
  const [detailLevel, setDetailLevel] = useState("balanced");
  const [familiarityLevel, setFamiliarityLevel] = useState("intermediate");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Track window size for responsive rendering
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSubmit = useCallback(() => {
    if (onSubmit) {
      onSubmit({
        customPrompt,
        selectedModel,
        detailLevel,
        familiarityLevel,
      });
    } else {
      // Default behavior if no onSubmit callback provided
      console.log("Refreshing summary with:", {
        customPrompt,
        selectedModel,
        detailLevel,
        familiarityLevel,
      });
      message.success("Summary refresh requested!");
    }

    // Reset form
    setCustomPrompt("");
    setSelectedModel("gpt-4.1");
    setDetailLevel("balanced");
    setFamiliarityLevel("intermediate");
    onClose();
  }, [
    customPrompt,
    selectedModel,
    detailLevel,
    familiarityLevel,
    onSubmit,
    onClose,
  ]);

  return (
    <>
      <Global styles={globalModalStyles} />
      <ConfigProvider
        theme={{
          components: {
            Select: {
              colorBgElevated: "#2a2a2a",
              colorText: "#f2f2f2",
              colorTextPlaceholder: "rgba(242, 242, 242, 0.5)",
              colorBorder: "rgba(75, 75, 75, 0.5)",
              colorPrimaryHover: "rgba(106, 120, 255, 0.6)",
              controlItemBgHover: "rgba(106, 120, 255, 0.2)",
              optionSelectedBg: "rgba(106, 120, 255, 0.3)",
            },
          },
        }}
      >
        <Modal
          title="Re-generate Summary"
          open={visible}
          onCancel={onClose}
          footer={null}
          centered
          wrapClassName="refresh-summary-modal-wrapper"
          css={modalOverlayStyle}
          width={Math.min(800, window.innerWidth - 32)}
        >
          <div css={modalContentStyle}>
            <div css={formFieldStyle}>
              <label css={fieldLabelStyle}>Level of Detail</label>
              {isMobile ? (
                <Select
                  css={selectStyle}
                  value={detailLevel}
                  onChange={(value) => setDetailLevel(value)}
                  options={[
                    { label: "Quick Overview", value: "quick_overview" },
                    { label: "Balanced", value: "balanced" },
                    { label: "Comprehensive", value: "comprehensive" },
                  ]}
                  dropdownStyle={{
                    background: "#2a2a2a",
                    border: "1px solid rgba(106, 120, 255, 0.3)",
                    borderRadius: "8px",
                  }}
                />
              ) : (
                <Segmented
                  css={segmentedStyle}
                  options={[
                    { label: "Quick Overview", value: "quick_overview" },
                    { label: "Balanced", value: "balanced" },
                    { label: "Comprehensive", value: "comprehensive" },
                  ]}
                  value={detailLevel}
                  onChange={(value) => setDetailLevel(value as string)}
                  block
                />
              )}
            </div>

            <div css={formFieldStyle}>
              <label css={fieldLabelStyle}>
                Your Familiarity with the Topic
              </label>
              {isMobile ? (
                <Select
                  css={selectStyle}
                  value={familiarityLevel}
                  onChange={(value) => setFamiliarityLevel(value)}
                  options={[
                    { label: "Beginner", value: "beginner" },
                    { label: "Intermediate", value: "intermediate" },
                    { label: "Advanced", value: "advanced" },
                  ]}
                  dropdownStyle={{
                    background: "#2a2a2a",
                    border: "1px solid rgba(106, 120, 255, 0.3)",
                    borderRadius: "8px",
                  }}
                />
              ) : (
                <Segmented
                  css={segmentedStyle}
                  options={[
                    { label: "Beginner", value: "beginner" },
                    { label: "Intermediate", value: "intermediate" },
                    { label: "Advanced", value: "advanced" },
                  ]}
                  value={familiarityLevel}
                  onChange={(value) => setFamiliarityLevel(value as string)}
                  block
                />
              )}
            </div>

            <div css={formFieldStyle}>
              <label css={fieldLabelStyle}>Model</label>
              <Select
                css={selectStyle}
                value={selectedModel}
                onChange={(value) => setSelectedModel(value)}
                options={[
                  {
                    label: "GPT-4.1 (Good quality, Fast)",
                    value: "gpt-4.1",
                  },
                  {
                    label: "GPT-5 (Higher quality, Slow: up to 1min)",
                    value: "gpt-5",
                  },
                ]}
                dropdownStyle={{
                  background: "#2a2a2a",
                  border: "1px solid rgba(106, 120, 255, 0.3)",
                  borderRadius: "8px",
                }}
              />
            </div>

            <div css={formFieldStyle}>
              <label css={fieldLabelStyle}>
                Custom Instructions (Optional)
              </label>
              <TextareaAutosize
                css={textareaStyle}
                minRows={4}
                maxRows={10}
                placeholder="e.g. focus on a specific topic or section, or any other custom request..."
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
              />
            </div>

            <button css={submitButtonStyle} onClick={handleSubmit}>
              Submit
            </button>
          </div>
        </Modal>
      </ConfigProvider>
    </>
  );
};

const globalModalStyles = css`
  .refresh-summary-modal-wrapper .ant-modal {
    max-width: 100%;
  }

  @media (max-width: 768px) {
    .refresh-summary-modal-wrapper .ant-modal {
      width: 98vw !important;
      max-width: 98vw;
    }
  }
`;

const modalOverlayStyle = css`
  .ant-modal-content {
    background: #1a1a1a;
    border-radius: 12px;
    border: 1px solid rgba(106, 120, 255, 0.3);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  }

  .ant-modal-header {
    background: #1a1a1a;
    border-bottom: 1px solid rgba(75, 75, 75, 0.3);
    border-radius: 12px 12px 0 0;
  }

  .ant-modal-title {
    color: #f2f2f2;
    font-size: 20px;
    font-weight: 600;
  }

  .ant-modal-close {
    color: #f2f2f2;
    &:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.1);
    }
  }

  .ant-modal-body {
    padding: 32px;
  }

  .ant-modal-footer {
    border-top: 1px solid rgba(75, 75, 75, 0.3);
    background: #1a1a1a;
    border-radius: 0 0 12px 12px;
  }

  @media (max-width: 768px) {
    .ant-modal-content {
      // margin: 2px;
    }

    .ant-modal-body {
      padding: 20px 6px;
    }

    .ant-modal-title {
      font-size: 18px;
    }
  }
`;

const modalContentStyle = css`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const formFieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const fieldLabelStyle = css`
  color: #f2f2f2;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.2px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const textareaStyle = css`
  width: 100%;
  min-height: 100px;
  padding: 14px;
  border-radius: 8px;
  border: 1px solid rgba(75, 75, 75, 0.5);
  background: #2a2a2a;
  color: #f2f2f2;
  font-size: 15px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: rgba(242, 242, 242, 0.5);
  }

  &:focus {
    outline: none;
    border-color: rgba(106, 120, 255, 0.6);
    box-shadow: 0 0 0 2px rgba(106, 120, 255, 0.15);
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px;
    min-height: 80px;
  }
`;

const selectStyle = css`
  width: 100%;

  .ant-select-selector {
    background: #2a2a2a !important;
    border: 1px solid rgba(75, 75, 75, 0.5) !important;
    border-radius: 8px !important;
    color: #f2f2f2 !important;
    padding: 10px 12px !important;
    height: auto !important;
    min-height: 42px !important;

    &:hover {
      border-color: rgba(106, 120, 255, 0.5) !important;
    }
  }

  .ant-select-selection-item {
    font-size: 15px;
  }

  .ant-select-focused .ant-select-selector {
    border-color: rgba(106, 120, 255, 0.6) !important;
    box-shadow: 0 0 0 2px rgba(106, 120, 255, 0.15) !important;
  }

  .ant-select-arrow {
    color: #f2f2f2;
  }

  @media (max-width: 768px) {
    .ant-select-selector {
      padding: 8px 12px !important;
      min-height: 38px !important;
    }

    .ant-select-selection-item {
      font-size: 14px;
    }
  }
`;

const segmentedStyle = css`
  background: #2a2a2a;
  border-radius: 8px;
  padding: 5px;

  .ant-segmented-item {
    color: rgba(242, 242, 242, 0.7);
    border-radius: 6px;
    transition: all 0.2s ease;
    padding: 10px 16px;
    font-size: 15px;

    &:hover {
      color: #f2f2f2;
    }
  }

  .ant-segmented-item-selected {
    background: linear-gradient(
      135deg,
      rgba(154, 83, 229, 0.3),
      rgba(106, 120, 255, 0.3)
    );
    color: #f2f2f2;
    font-weight: 500;
  }

  .ant-segmented-thumb {
    background: linear-gradient(
      135deg,
      rgba(154, 83, 229, 0.5),
      rgba(106, 120, 255, 0.5)
    );
    border-radius: 6px;
  }

  @media (max-width: 768px) {
    padding: 4px;

    .ant-segmented-item {
      padding: 8px 12px;
      font-size: 13px;
    }
  }
`;

const submitButtonStyle = css`
  width: 100%;
  padding: 14px 20px;
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  color: #f2f2f2;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  background: linear-gradient(
    135deg,
    rgba(154, 83, 229, 0.7),
    rgba(106, 120, 255, 0.7)
  );
  border: 1px solid transparent;
  box-shadow: 0 2px 8px rgba(106, 120, 255, 0.3);
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(154, 83, 229, 0.85),
      rgba(106, 120, 255, 0.85)
    );
    box-shadow: 0 4px 16px rgba(106, 120, 255, 0.4);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(106, 120, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 15px;
  }
`;
